import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth, firstAllowedPath } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { EnvelopeSimple, ArrowLeft } from "@phosphor-icons/react";

export default function VerifyOtp() {
  const navigate = useNavigate();
  const { finishLogin } = useAuth();
  const email = sessionStorage.getItem("viva_pending_email");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!email) navigate("/login");
  }, [email, navigate]);

  const verify = async (value) => {
    const finalCode = value || code;
    if (finalCode.length !== 6) return;
    setLoading(true);
    try {
      const res = await api.post("/auth/verify-otp", { email, code: finalCode });
      sessionStorage.removeItem("viva_pending_email");
      finishLogin(res.data.access_token, res.data.user);
      toast.success("Signed in");
      navigate(firstAllowedPath(res.data.user));
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
      setCode("");
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    try {
      await api.post("/auth/resend-otp", { email });
      toast.success("A new code was sent to your email");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background grid-bg p-8">
      <div className="w-full max-w-md">
        <div className="bg-white border border-zinc-300 p-8 hard-shadow">
          <div className="w-12 h-12 bg-primary flex items-center justify-center mb-6">
            <EnvelopeSimple size={24} weight="bold" className="text-white" />
          </div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Two-Step Verification</p>
          <h1 className="font-heading text-3xl font-black uppercase tracking-tighter text-zinc-900 mt-1">Enter Code</h1>
          <p className="text-sm text-zinc-500 mt-2 mb-6">
            We sent a 6-digit code to <span className="font-semibold text-zinc-800">{email}</span>
          </p>

          <div className="flex justify-center mb-6" data-testid="otp-input">
            <InputOTP maxLength={6} value={code} onChange={(v) => { setCode(v); if (v.length === 6) verify(v); }}>
              <InputOTPGroup>
                {[0, 1, 2, 3, 4, 5].map((i) => (
                  <InputOTPSlot key={i} index={i} className="rounded-none border-zinc-300 w-11 h-12 text-lg font-mono" />
                ))}
              </InputOTPGroup>
            </InputOTP>
          </div>

          <Button
            onClick={() => verify()}
            disabled={loading || code.length !== 6}
            data-testid="otp-verify-button"
            className="w-full rounded-none bg-primary hover:bg-[#C2410C] text-white font-bold uppercase tracking-wider h-11 transition-colors duration-100"
          >
            {loading ? "Verifying..." : "Verify & Sign In"}
          </Button>

          <div className="flex items-center justify-between mt-5 text-xs">
            <button onClick={() => navigate("/login")} data-testid="otp-back-button" className="flex items-center gap-1 text-zinc-500 hover:text-zinc-900">
              <ArrowLeft size={14} weight="bold" /> Back to login
            </button>
            <button onClick={resend} className="text-primary font-semibold hover:underline">Resend code</button>
          </div>
        </div>
      </div>
    </div>
  );
}
